import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, ExternalLink, CalendarClock } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import PageShell, { PageHead } from "@/components/pages/PageShell";
import { StoreBadges } from "@/components/landing/StoreLinks";
import { PartnerBand } from "@/components/landing/PartnerBits";
import { EXPO } from "@/config/links";

const OpenButtons = () => {
  const { t } = useLang();
  const g = t.pages.getApp;
  const [copied, setCopied] = useState(false);
  const copy = () => navigator.clipboard?.writeText(EXPO.url).then(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  });
  return (
    <div className="flex flex-wrap gap-3">
      <a href={EXPO.url} data-testid="get-app-open-expo" className="inline-flex items-center gap-2 rounded-full bg-moss hover:bg-moss-dark text-parchment px-6 py-3 font-extrabold transition-colors">
        <ExternalLink className="w-4 h-4" /> {g.open}
      </a>
      <button type="button" onClick={copy} data-testid="get-app-copy-link" className="inline-flex items-center gap-2 rounded-full border-2 border-ink px-5 py-2.5 font-extrabold text-ink hover:bg-ink hover:text-parchment transition-colors">
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? g.copied : g.copyLink}
      </button>
    </div>
  );
};

const DownloadBanner = () => {
  const { t } = useLang();
  const g = t.pages.getApp;
  return (
    <div data-testid="get-app-download-banner" className="mt-8 journal-card bg-paper/90 p-6 md:p-8 border-2 border-moss/30">
      <h2 className="font-display text-2xl md:text-3xl font-semibold text-ink">{g.dlTitle}</h2>
      <div className="mt-6 text-xs font-extrabold uppercase tracking-[0.16em] text-amber-dark">{g.dlExpo}</div>
      <div className="mt-3">
        <StoreBadges idPrefix="get-app" ios={EXPO.ios} android={EXPO.android} />
      </div>
      <div className="mt-7 text-xs font-extrabold uppercase tracking-[0.16em] text-amber-dark">{g.dlOpen}</div>
      <div className="mt-3 rounded-2xl bg-parchment border border-line px-4 py-3">
        <div className="text-xs font-bold text-ink-muted">{g.dlLink}</div>
        <a href={EXPO.url} data-testid="get-app-direct-link" className="mt-1 block font-mono text-sm break-all text-moss underline decoration-moss/40 underline-offset-4 hover:decoration-moss">
          {EXPO.url}
        </a>
      </div>
      <div className="mt-4">
        <OpenButtons />
      </div>
    </div>
  );
};

const QrCard = () => {
  const { t } = useLang();
  const g = t.pages.getApp;
  return (
    <div data-testid="get-app-qr-card" className="journal-card p-8 mx-auto max-w-sm text-center rotate-[1deg]">
      <div className="mx-auto w-fit rounded-2xl bg-white p-4 border border-line" data-testid="get-app-qr">
        <QRCodeSVG value={EXPO.url} size={232} fgColor="#2E2A25" bgColor="#FFFFFF" level="M" title={g.qrAlt} />
      </div>
      <p className="mt-5 font-display text-lg font-semibold text-ink">{g.qrCaption}</p>
    </div>
  );
};

const Step = ({ n, title, body, children }) => (
  <li className="relative border-t-2 border-dashed border-line pt-6" data-testid={`get-app-step-${n}`}>
    <span className="font-display text-5xl font-semibold text-amber/80 tabular-nums">0{n}</span>
    <h2 className="mt-3 font-display text-xl font-semibold text-ink">{title}</h2>
    <p className="mt-2 text-ink-muted leading-relaxed">{body}</p>
    {children && <div className="mt-5">{children}</div>}
  </li>
);

export default function GetAppPage() {
  const { t } = useLang();
  const g = t.pages.getApp;
  return (
    <PageShell testId="get-app-page">
      <div className="space-y-20 md:space-y-28">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <PageHead kicker={g.kicker} title={g.title} lead={g.lead}>
              <p data-testid="get-app-launch-note" className="mt-7 inline-flex items-center gap-2 rounded-full bg-amber/15 border border-amber/40 px-4 py-2 text-sm font-extrabold text-amber-dark">
                <CalendarClock className="w-4 h-4" /> {g.notice}
              </p>
            </PageHead>
            <DownloadBanner />
          </div>
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <QrCard />
          </div>
        </div>
        <ol className="grid md:grid-cols-3 gap-10">
          <Step n={1} title={g.step1} body={g.step1Body} />
          <Step n={2} title={g.step2} body={g.step2Body} />
          <Step n={3} title={g.step3} body={g.step3Body} />
        </ol>
        <PartnerBand testId="get-app-partner-band" />
      </div>
    </PageShell>
  );
}
